'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { Flame, Trophy, ArrowLeft, Star, Crown, Clock, Award, Plus, X, Check } from 'lucide-react';

interface LegacyRoaster {
  id: string;
  displayName: string;
  username: string;
  country: string;
  countryFlag: string;
  initials: string;
  avatarColor: string;
  era: string;
  peakRank: number;
  totalPoints: number;
  wins: number;
  signature: string;
  retiredAt: string;
  badges: string[];
}

const INITIAL_LEGENDS: LegacyRoaster[] = [];

const AVATAR_COLORS = ['#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#22c55e', '#f97316', '#a855f7', '#e11d48'];

function Avatar({ initials, color, size = 48 }: { initials: string; color: string; size?: number }) {
  return (
    <div className="rounded-full flex items-center justify-center font-bold text-white flex-shrink-0"
      style={{ width: size, height: size, background: color, fontSize: size * 0.35 }}>
      {initials}
    </div>
  );
}

function AddLegendForm({ onAdd, onCancel }: { onAdd: (r: LegacyRoaster) => void; onCancel: () => void }) {
  const [form, setForm] = useState({
    displayName: '', username: '', country: '', countryFlag: '',
    era: '', peakRank: '', totalPoints: '', wins: '', signature: '', retiredAt: '',
  });

  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }));

  const inputStyle = {
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid var(--border)',
    color: 'var(--foreground)',
    borderRadius: '10px',
    padding: '8px 12px',
    fontSize: '13px',
    width: '100%',
    outline: 'none',
  };

  const handleSubmit = () => {
    if (!form.displayName.trim()) return;
    const initials = form.displayName.trim().split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
    const color = AVATAR_COLORS[Math.floor(Math.random() * AVATAR_COLORS.length)];
    onAdd({
      id: `leg-${Date.now()}`,
      displayName: form.displayName.trim(),
      username: form.username.trim() || `@${form.displayName.toLowerCase().replace(/\s+/g, '_')}`,
      country: form.country.trim() || 'Unknown',
      countryFlag: form.countryFlag.trim() || '🌍',
      initials,
      avatarColor: color,
      era: form.era.trim() || 'Unknown Era',
      peakRank: parseInt(form.peakRank) || 1,
      totalPoints: parseInt(form.totalPoints) || 0,
      wins: parseInt(form.wins) || 0,
      signature: form.signature.trim() || '',
      retiredAt: form.retiredAt.trim() || 'Unknown',
      badges: ['👑'],
    });
  };

  return (
    <div className="card-glass p-5 rounded-2xl mb-8" style={{ border: '1px solid rgba(245,158,11,0.25)' }}>
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm font-bold" style={{ color: '#f59e0b' }}>✏️ Add Legend</span>
        <button onClick={onCancel} className="p-1 rounded-lg hover:bg-white/5" style={{ color: 'var(--muted-foreground)' }}><X size={14} /></button>
      </div>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <input style={inputStyle} placeholder="Display Name *" value={form.displayName} onChange={e => set('displayName', e.target.value)} />
          <input style={inputStyle} placeholder="@username" value={form.username} onChange={e => set('username', e.target.value)} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <input style={inputStyle} placeholder="Country (e.g. Nigeria)" value={form.country} onChange={e => set('country', e.target.value)} />
          <input style={inputStyle} placeholder="Flag emoji (e.g. 🇳🇬)" value={form.countryFlag} onChange={e => set('countryFlag', e.target.value)} />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <input style={inputStyle} placeholder="Era (e.g. 2019–2021)" value={form.era} onChange={e => set('era', e.target.value)} />
          <input style={inputStyle} placeholder="Peak Rank (e.g. 1)" type="number" value={form.peakRank} onChange={e => set('peakRank', e.target.value)} />
          <input style={inputStyle} placeholder="Retired At (e.g. Dec 2021)" value={form.retiredAt} onChange={e => set('retiredAt', e.target.value)} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <input style={inputStyle} placeholder="Total Points" type="number" value={form.totalPoints} onChange={e => set('totalPoints', e.target.value)} />
          <input style={inputStyle} placeholder="Total Wins" type="number" value={form.wins} onChange={e => set('wins', e.target.value)} />
        </div>
        <input style={inputStyle} placeholder={"Signature quote (e.g. \"I don't roast, I cremate.\")"} value={form.signature} onChange={e => set('signature', e.target.value)} />
        <button onClick={handleSubmit} disabled={!form.displayName.trim()}
          className="flex items-center gap-2 px-4 py-2 rounded-xl font-bold text-sm transition-all duration-200 hover:scale-105 disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ background: 'linear-gradient(135deg, #b45309, #f59e0b)', color: '#fff' }}>
          <Check size={14} />
          Add to Legacy
        </button>
      </div>
    </div>
  );
}

export default function LegacyLeaderboardPage() {
  const [legends, setLegends] = useState<LegacyRoaster[]>([]);
  const [showAdd, setShowAdd] = useState(false);

  const addLegend = (r: LegacyRoaster) => {
    setLegends(prev => [...prev, r]);
    setShowAdd(false);
  };

  return (
    <div className="min-h-screen pb-24 md:pb-8" style={{ background: 'var(--background)' }}>
      {/* Navbar */}
      <header className="sticky top-0 z-50 w-full" style={{ background: 'rgba(10,10,15,0.92)', backdropFilter: 'blur(20px)', borderBottom: '1px solid var(--border)' }}>
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link href="/roastrank" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #16a34a, #22c55e)' }}>
                <Flame size={18} className="text-white" />
              </div>
              <span className="font-bold text-lg" style={{ color: 'var(--foreground)' }}>Roast<span style={{ color: 'var(--primary)' }}>Rank</span></span>
            </Link>
            <nav className="hidden md:flex items-center gap-1">
              {[
                { label: 'Home', href: '/roastrank' },
                { label: 'Leaderboard', href: '/roastrank/leaderboard' },
                { label: 'Browse', href: '/roastrank/browse' },
                { label: 'Submit', href: '/roastrank/submit' },
                { label: '🏛️ Legacy', href: '/roastrank/legacy' },
              ].map(item => (
                <Link key={item.href} href={item.href} className="px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 hover:bg-white/5"
                  style={{ color: item.href === '/roastrank/legacy' ? '#f59e0b' : 'var(--muted-foreground)' }}>
                  {item.label}
                </Link>
              ))}
            </nav>
            <Link href="/roastrank/leaderboard" className="flex items-center gap-2 px-3 py-2 rounded-xl font-semibold text-sm transition-all duration-200 hover:bg-white/5"
              style={{ color: 'var(--muted-foreground)', border: '1px solid var(--border)' }}>
              <ArrowLeft size={14} />
              <span className="hidden sm:inline">Back</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden py-16 px-4 sm:px-6 lg:px-8">
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at center, rgba(245,158,11,0.06) 0%, transparent 70%)' }} />
        <div className="max-w-screen-xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 text-sm font-semibold"
            style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.25)', color: '#f59e0b' }}>
            <Clock size={14} />
            Retired Legends · WhatsApp Roast History
          </div>
          <h1 className="text-4xl md:text-5xl font-black mb-4" style={{ color: 'var(--foreground)' }}>
            🏛️ Legacy<br />
            <span style={{ background: 'linear-gradient(135deg, #f59e0b, #fbbf24)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
              Leaderboard
            </span>
          </h1>
          <p className="text-lg mb-6 max-w-xl mx-auto" style={{ color: 'var(--muted-foreground)' }}>
            The greatest WhatsApp roasters who ever lived. Retired from the game, but never forgotten.
          </p>
          <div className="inline-flex items-center gap-3 px-5 py-3 rounded-2xl"
            style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)' }}>
            <Trophy size={16} style={{ color: '#f59e0b' }} />
            <span className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>
              {legends.length} <span style={{ color: '#f59e0b' }}>Legends</span> enshrined
            </span>
          </div>
        </div>
      </section>

      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 space-y-8">

        {/* Add Legend Button */}
        {!showAdd && (
          <div className="flex justify-end">
            <button onClick={() => setShowAdd(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl font-semibold text-sm transition-all duration-200 hover:scale-105"
              style={{ background: 'rgba(245,158,11,0.1)', color: '#f59e0b', border: '1px solid rgba(245,158,11,0.25)' }}>
              <Plus size={14} />
              Add Legend
            </button>
          </div>
        )}

        {/* Add Form */}
        {showAdd && <AddLegendForm onAdd={addLegend} onCancel={() => setShowAdd(false)} />}

        {/* Top Legend Spotlight */}
        {legends.length > 0 && (
          <div className="relative rounded-3xl overflow-hidden p-6 md:p-8"
            style={{ background: 'linear-gradient(135deg, rgba(245,158,11,0.08), rgba(251,191,36,0.04))', border: '1px solid rgba(245,158,11,0.25)' }}>
            <div className="absolute top-4 right-4 text-6xl opacity-10 pointer-events-none">👑</div>
            <div className="flex items-center gap-2 mb-6">
              <Crown size={18} style={{ color: '#f59e0b' }} />
              <span className="font-bold text-sm" style={{ color: '#f59e0b' }}>Greatest of All Time</span>
            </div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
              <div className="relative flex-shrink-0">
                <div className="rounded-full p-1" style={{ background: 'linear-gradient(135deg, #f59e0b, #fbbf24)' }}>
                  <Avatar initials={legends[0].initials} color={legends[0].avatarColor} size={80} />
                </div>
                <div className="absolute -bottom-1 -right-1 text-2xl">👑</div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-3 mb-1">
                  <h2 className="text-2xl font-bold" style={{ color: 'var(--foreground)' }}>{legends[0].displayName}</h2>
                  <span className="text-xl">{legends[0].countryFlag}</span>
                  <span className="text-xs px-2 py-1 rounded-full font-bold" style={{ background: 'rgba(245,158,11,0.15)', color: '#f59e0b', border: '1px solid rgba(245,158,11,0.3)' }}>
                    RETIRED
                  </span>
                </div>
                <div className="text-sm mb-2" style={{ color: 'var(--muted-foreground)' }}>{legends[0].username} · {legends[0].country} · Era: {legends[0].era}</div>
                {legends[0].signature && (
                  <p className="text-sm italic mb-3" style={{ color: 'var(--foreground)' }}>{legends[0].signature}</p>
                )}
                <div className="flex flex-wrap gap-3">
                  <div className="text-center px-3 py-2 rounded-xl" style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.2)' }}>
                    <div className="font-bold text-lg" style={{ color: '#f59e0b' }}>#{legends[0].peakRank}</div>
                    <div className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Peak Rank</div>
                  </div>
                  <div className="text-center px-3 py-2 rounded-xl" style={{ background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.2)' }}>
                    <div className="font-bold text-lg" style={{ color: 'var(--primary)' }}>{legends[0].totalPoints.toLocaleString()}</div>
                    <div className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Total Points</div>
                  </div>
                  <div className="text-center px-3 py-2 rounded-xl" style={{ background: 'rgba(139,92,246,0.1)', border: '1px solid rgba(139,92,246,0.2)' }}>
                    <div className="font-bold text-lg" style={{ color: '#8b5cf6' }}>{legends[0].wins}</div>
                    <div className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Total Wins</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* All Legends Table */}
        <div>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(245,158,11,0.15)' }}>
              <Award size={16} style={{ color: '#f59e0b' }} />
            </div>
            <div>
              <h2 className="text-xl font-bold" style={{ color: 'var(--foreground)' }}>All Legends</h2>
              <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Retired WhatsApp roasters who shaped the game</p>
            </div>
          </div>

          {legends.length === 0 ? (
            <div className="card-glass rounded-2xl p-12 text-center" style={{ border: '1px dashed rgba(245,158,11,0.2)' }}>
              <div className="text-4xl mb-3">🏛️</div>
              <h3 className="font-bold mb-2" style={{ color: 'var(--foreground)' }}>No legends yet</h3>
              <p className="text-sm mb-4" style={{ color: 'var(--muted-foreground)' }}>Add the first retired WhatsApp roaster to the Legacy Leaderboard.</p>
              <button onClick={() => setShowAdd(true)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl font-bold text-sm transition-all duration-200 hover:scale-105"
                style={{ background: 'rgba(245,158,11,0.1)', color: '#f59e0b', border: '1px solid rgba(245,158,11,0.2)' }}>
                <Plus size={14} />
                Add First Legend
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {legends.map((legend, idx) => (
                <div key={legend.id} className="card-glass rounded-2xl p-5 transition-all duration-200 hover:bg-white/5"
                  style={{ border: idx === 0 ? '1px solid rgba(245,158,11,0.3)' : '1px solid var(--border)' }}>
                  <div className="flex items-start gap-4">
                    {/* Rank */}
                    <div className="w-10 text-center flex-shrink-0 pt-1">
                      <div className="font-mono font-bold text-lg" style={{ color: idx === 0 ? '#f59e0b' : 'var(--muted-foreground)' }}>
                        {idx === 0 ? '👑' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `#${idx + 1}`}
                      </div>
                    </div>

                    {/* Avatar */}
                    <Avatar initials={legend.initials} color={legend.avatarColor} size={48} />

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className="font-bold" style={{ color: 'var(--foreground)' }}>{legend.displayName}</span>
                        <span className="text-lg">{legend.countryFlag}</span>
                        <span className="text-xs px-2 py-0.5 rounded-full font-bold" style={{ background: 'rgba(245,158,11,0.1)', color: '#f59e0b', border: '1px solid rgba(245,158,11,0.25)' }}>
                          RETIRED {legend.retiredAt}
                        </span>
                        <div className="flex gap-1">
                          {legend.badges.map((b, i) => <span key={i} className="text-sm">{b}</span>)}
                        </div>
                      </div>
                      <div className="text-xs mb-2" style={{ color: 'var(--muted-foreground)' }}>
                        {legend.username} · {legend.country} · Era: {legend.era}
                      </div>
                      {legend.signature && (
                        <p className="text-xs italic mb-3" style={{ color: 'var(--muted-foreground)' }}>{legend.signature}</p>
                      )}
                      <div className="flex flex-wrap gap-3">
                        <div className="flex items-center gap-1.5">
                          <Star size={12} style={{ color: '#f59e0b' }} />
                          <span className="text-xs font-bold" style={{ color: 'var(--foreground)' }}>Peak #{legend.peakRank}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Trophy size={12} style={{ color: 'var(--primary)' }} />
                          <span className="text-xs font-bold" style={{ color: 'var(--foreground)' }}>{legend.totalPoints.toLocaleString()} pts</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Award size={12} style={{ color: '#8b5cf6' }} />
                          <span className="text-xs font-bold" style={{ color: 'var(--foreground)' }}>{legend.wins} wins</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* CTA */}
        <div className="text-center py-10 rounded-3xl" style={{ background: 'linear-gradient(135deg, rgba(245,158,11,0.08), rgba(251,191,36,0.04))', border: '1px solid rgba(245,158,11,0.2)' }}>
          <div className="text-4xl mb-3">🔥</div>
          <h3 className="text-xl font-bold mb-2" style={{ color: 'var(--foreground)' }}>Write Your Own Legacy</h3>
          <p className="text-sm mb-6 max-w-md mx-auto" style={{ color: 'var(--muted-foreground)' }}>
            The legends above were once where you are now. Submit your roasts, earn votes, and build a legacy worth remembering.
          </p>
          <Link href="/roastrank/submit"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl font-bold text-sm transition-all duration-300 hover:scale-105"
            style={{ background: 'linear-gradient(135deg, #16a34a, #22c55e)', color: '#fff', boxShadow: '0 0 20px rgba(34,197,94,0.25)' }}>
            <Flame size={16} />
            Submit Your Roast
          </Link>
        </div>
      </div>
    </div>
  );
}
